import React, { useEffect, useRef, useState } from 'react';
import './TerminalPreview.css';

interface OrbitSDK {
  music: {
    play: () => void;
    pause: () => void;
    toggle: () => void;
    next: () => void;
    prev: () => void;
    getCurrentTrack: () => { name: string; index: number };
    isPlaying: () => boolean;
    playTrack: (index: number) => void;
  };
  apps: {
    openMusicPlayer: () => void;
    openPhotoBooth: () => void;
    openGarden: () => void;
    openGuestbook: () => void;
    openOrbitTV: () => void;
    openGenius: () => void;
    openFileBrowser: (folder?: string) => void;
  };
  user: {
    handle: string | null;
    isLoggedIn: boolean;
  };
  notify: (message: string) => void;
  storage: {
    get: (key: string) => any;
    set: (key: string, value: any) => void;
    clear: () => void;
  };
}

interface HtmlAppFrameProps {
  html: string;
  appId: string;
  appName: string;
  appEmoji: string;
  orbitSDK: OrbitSDK;
}

const SANDBOX_PERMS = 'allow-scripts allow-downloads allow-popups allow-forms allow-modals allow-pointer-lock';

// Build the orbit SDK shim script injected into html-v2 apps. Mirrors the SDK
// available to jsx apps: postMessage bridge for music/apps/notify/storage.
const buildOrbitShim = (userJson: string, encodedStore: string): string => {
  return `<script>
(function() {
  var storeData = {};
  try { storeData = JSON.parse(decodeURIComponent(escape(atob('${encodedStore}')))) || {}; } catch (e) { storeData = {}; }
  window.orbit = {
    music: {
      play: function() { window.parent.postMessage({ type: 'sdk', action: 'music.play' }, '*'); },
      pause: function() { window.parent.postMessage({ type: 'sdk', action: 'music.pause' }, '*'); },
      toggle: function() { window.parent.postMessage({ type: 'sdk', action: 'music.toggle' }, '*'); },
      next: function() { window.parent.postMessage({ type: 'sdk', action: 'music.next' }, '*'); },
      prev: function() { window.parent.postMessage({ type: 'sdk', action: 'music.prev' }, '*'); },
      playTrack: function(i) { window.parent.postMessage({ type: 'sdk', action: 'music.playTrack', data: i }, '*'); },
      getCurrentTrack: function() { return null; },
      isPlaying: function() { return false; }
    },
    apps: {
      openMusicPlayer: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openMusicPlayer' }, '*'); },
      openPhotoBooth: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openPhotoBooth' }, '*'); },
      openGarden: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGarden' }, '*'); },
      openGuestbook: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGuestbook' }, '*'); },
      openOrbitTV: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openOrbitTV' }, '*'); },
      openGenius: function() { window.parent.postMessage({ type: 'sdk', action: 'apps.openGenius' }, '*'); },
      openFileBrowser: function(f) { window.parent.postMessage({ type: 'sdk', action: 'apps.openFileBrowser', data: f }, '*'); }
    },
    user: ${userJson},
    notify: function(message) { window.parent.postMessage({ type: 'sdk', action: 'notify', data: message }, '*'); },
    storage: {
      get: function(key) { return Object.prototype.hasOwnProperty.call(storeData, key) ? storeData[key] : null; },
      set: function(key, value) {
        storeData[key] = value;
        window.parent.postMessage({ type: 'sdk', action: 'storage.set', data: { key: key, value: value } }, '*');
      },
      clear: function() {
        storeData = {};
        window.parent.postMessage({ type: 'sdk', action: 'storage.clear' }, '*');
      }
    }
  };
})();
<` + `/script>`;
};

// Inject the shim as early as possible so it runs before the app's own scripts
export const injectOrbitShim = (html: string, shim: string): string => {
  const headMatch = html.match(/<head[^>]*>/i);
  if (headMatch && headMatch.index !== undefined) {
    const insertAt = headMatch.index + headMatch[0].length;
    return html.slice(0, insertAt) + shim + html.slice(insertAt);
  }
  return shim + html;
};

const HtmlAppFrame: React.FC<HtmlAppFrameProps> = ({ html, appId, appName, appEmoji, orbitSDK }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!iframeRef.current) return;
    setIsLoading(true);

    const storagePrefix = `orbit_app_${appId}_`;
    const storeSnapshot: Record<string, any> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf(storagePrefix) === 0) {
          try {
            storeSnapshot[key.slice(storagePrefix.length)] = JSON.parse(localStorage.getItem(key) || 'null');
          } catch (e) { /* skip */ }
        }
      }
    } catch (e) { /* localStorage unavailable */ }
    const encodedStore = btoa(unescape(encodeURIComponent(JSON.stringify(storeSnapshot))));

    const shim = buildOrbitShim(JSON.stringify(orbitSDK.user), encodedStore);
    const iframe = iframeRef.current;
    iframe.setAttribute('srcdoc', injectOrbitShim(html, shim));

    const loadHandler = () => setIsLoading(false);
    iframe.addEventListener('load', loadHandler);

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;
      const { type, action, data } = event.data || {};
      if (type !== 'sdk') return;

      switch (action) {
        case 'music.play': orbitSDK.music.play(); break;
        case 'music.pause': orbitSDK.music.pause(); break;
        case 'music.toggle': orbitSDK.music.toggle(); break;
        case 'music.next': orbitSDK.music.next(); break;
        case 'music.prev': orbitSDK.music.prev(); break;
        case 'music.playTrack': orbitSDK.music.playTrack(data); break;
        case 'apps.openMusicPlayer': orbitSDK.apps.openMusicPlayer(); break;
        case 'apps.openPhotoBooth': orbitSDK.apps.openPhotoBooth(); break;
        case 'apps.openGarden': orbitSDK.apps.openGarden(); break;
        case 'apps.openGuestbook': orbitSDK.apps.openGuestbook(); break;
        case 'apps.openOrbitTV': orbitSDK.apps.openOrbitTV(); break;
        case 'apps.openGenius': orbitSDK.apps.openGenius(); break;
        case 'apps.openFileBrowser': orbitSDK.apps.openFileBrowser(data); break;
        case 'notify': orbitSDK.notify(data); break;
        case 'storage.set':
          try {
            localStorage.setItem(storagePrefix + data.key, JSON.stringify(data.value));
          } catch (e) { console.error('Storage error:', e); }
          break;
        case 'storage.clear':
          try {
            Object.keys(localStorage).forEach(key => {
              if (key.indexOf(storagePrefix) === 0) localStorage.removeItem(key);
            });
          } catch (e) { console.error('Storage error:', e); }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    const timeout = setTimeout(() => setIsLoading(false), 5000);

    return () => {
      iframe.removeEventListener('load', loadHandler);
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, appId, orbitSDK]);

  return (
    <div className="terminal-preview">
      {isLoading && (
        <div className="preview-loading">
          <span className="loading-spinner">◐</span> Loading {appEmoji} {appName}...
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={`${appEmoji} ${appName}`}
        className="preview-iframe"
        sandbox={SANDBOX_PERMS}
        style={{ opacity: isLoading ? 0 : 1 }}
      />
    </div>
  );
};

export default HtmlAppFrame;
